import { Component } from "@theme/component";
import { morph } from "@theme/morph";
import { sectionRenderer } from "@theme/section-renderer";
import { StandardEvents } from "@shopify/events";

/**
 * Keeps the product detail table in sync with the selected variant.
 * If the block is in the same section as the variant selection, it uses the supplied HTML.
 * Otherwise, it reloads only its own section and updates the block specifically.
 */
class ProductDetails extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      {
        signal: this.#abortController.signal,
      },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  get #sectionId() {
    return this.closest(".shopify-section")?.id;
  }

  /**
   * Takes the rows of this block from the re-rendered section.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    // Ignore swatches in product cards and quick add dialogs
    if (
      target.closest("product-card") ||
      target.closest("dialog") !== this.closest("dialog")
    )
      return;

    event.promise
      .then(async ({ detail }) => {
        // resource is null when the option combination does not exist
        const variantId = detail?.resource?.id;
        if (!variantId) return;

        const { newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (
          detail.productId &&
          detail.productId !== this.dataset.productId
        ) {
          return;
        }

        const newDetails = await this.#getNewDetails(
          target,
          detail.html,
          variantId,
        );
        if (newDetails) morph(this, newDetails, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== "AbortError")
          console.warn("[product-details] Event promise rejected:", error);
      });
  };

  /**
   * Reuses the picker's HTML when it contains this block, otherwise renders this section for the variant.
   * @param {Element} target
   * @param {Document | undefined} html
   * @param {string} variantId
   */
  async #getNewDetails(target, html, variantId) {
    const selector = `lc-product-details[data-block-id="${this.dataset.blockId}"]`;
    const sectionId = this.#sectionId;

    if (html && target.closest(".shopify-section")?.id === sectionId)
      return html.querySelector(selector);
    if (!sectionId) return null;

    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);

    const sectionHTML = await sectionRenderer.getSectionHTML(
      sectionId,
      true,
      url,
    );
    return new DOMParser()
      .parseFromString(sectionHTML, "text/html")
      .querySelector(selector);
  }
}

if (!customElements.get("lc-product-details")) {
  customElements.define("lc-product-details", ProductDetails);
}

